import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Boxes, Loader2, ExternalLink, CircleAlert, Lock, Undo2 } from 'lucide-react';
import { fetchAccesos, devolverActivo } from '../../../peticiones_API/PeticionesFichaAccesos';

/**
 * Fase 13 · §5.4 — la pestaña de Inventario.
 *
 * ── DE SOLO LECTURA, Y ES UNA DECISIÓN ────────────────────────────────────────────────────
 *
 * Asignar y devolver material es del **módulo de Inventario**, no de la ficha. Aquí se consulta y
 * se enlaza. Por eso los dos botones de guardar de la barra salen **deshabilitados con su motivo**
 * en esta pestaña: ver `TABS_SOLO_LECTURA` en `FormEditarPersona`.
 *
 * ── «NO TIENE» NO ES «NO LO SABEMOS» ──────────────────────────────────────────────────────
 *
 * Para una colaboradora que no existe en `personas`, la clave ajena de Inventario **impide** que
 * tenga material registrado. Una lista vacía ahí no significa que no tenga nada: significa que
 * Inventario no puede saberlo.
 *
 * El servidor lo dice con `material.fiable` y `material.motivo_no_fiable`, escritos en Bajas. **Se
 * pintan tal cual, no se reescriben**: dos versiones de la misma advertencia acabarían diciendo
 * cosas distintas.
 *
 * ── «PENDIENTE DE DEVOLUCIÓN» SÓLO CON BAJA ABIERTA ───────────────────────────────────────
 *
 * Tener material asignado es lo normal. Lo que lo convierte en pendiente es que la persona se esté
 * yendo. Sin baja abierta, «pendiente» sería una alarma que suena para toda la plantilla — y una
 * alarma que suena siempre no avisa de nada.
 */

/**
 * El enlace al activo, **comprobado pulsándolo** en el reconocimiento:
 * `/inventario/<scope>/<slug>/<id>` abre el panel de ese activo.
 *
 * El `scope` es el mismo que `queTiene` ya usa para leer `inv_asset_events` (`interno` | `colab`),
 * así que no hay que traducir nada — y traducir es donde se pierden estas cosas.
 */
const enlaceActivo = (ambito, slug, id) =>
  `/inventario/${ambito === 'colaborador' ? 'colab' : 'interno'}/${slug}/${id}`;

/** Qué significa el último evento de un activo, en palabras. Desde el 19/10 `queTiene` sólo
 *  devuelve el evento de posesión que lo decide (`inventario/services/posesion.js`): una asignación
 *  o, con lo devuelto, una devolución, que se pinta aparte. Ya no llegan notas ni reparaciones. */
const ETIQUETA_EVENTO = {
  asignacion: 'asignado',
};

const soloFecha = (f) => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : '—');

export function PestanaInventario({
  personaId, tipo = null, bajaAbierta = null, alContar = null, puedeEditar = false,
}) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const d = await fetchAccesos(personaId, tipo);
      setDatos(d);
      if (alContar) alContar(d.resumen);
    } catch (e) {
      toast.error('No se pudo cargar el material asignado.');
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, [personaId, tipo, alContar]);

  useEffect(() => { if (personaId) cargar(); }, [personaId, cargar]);

  const material = datos?.material;
  const items = material?.items || [];
  // Se separan para contar: el título dice lo que TIENE, y aparte cuántos devolvió. Un solo número
  // que sube al devolver cosas diría lo contrario de lo que significa.
  const enSuPoder = items.filter((m) => !m.devuelto);
  const devueltos = items.filter((m) => m.devuelto);
  const ambito = datos?.titular?.ambito || (tipo === 'colaboradora' ? 'colaborador' : 'interno');

  const [devolviendo, setDevolviendo] = useState(null);

  /**
   * Desasignar un activo. **Acción inmediata con confirmación, no atada a «Guardar».**
   *
   * Como revocar una credencial: tener acciones no es tener guardado, y por eso los dos botones
   * de la barra siguen deshabilitados en esta pestaña. Atarla a «Guardar» convertiría un botón en
   * dos cosas distintas.
   *
   * Va por el atajo del módulo de Inventario, con **su** permiso por tipo de activo. Un 403 aquí
   * significa que esa persona no puede devolver activos DE ESE TIPO, y el mensaje lo dice tal
   * cual: inventarse uno propio taparía la razón real.
   */
  const devolver = async (m) => {
    const nombre = `${m.tipo_nombre}${m.referencia ? ` · ${m.referencia}` : ''}`;
    if (!window.confirm(`¿Devolver «${nombre}»?

`
      + 'El activo quedará disponible y la asignación seguirá constando en su histórico.')) return;
    setDevolviendo(`${m.asset_type_slug}-${m.asset_id}`);
    try {
      await devolverActivo(m.asset_type_slug, m.asset_id, ambito);
      await cargar();
      toast.success('Activo devuelto. Queda en el histórico del activo.');
    } catch (e) {
      const st = e?.response?.status;
      toast.error(st === 403
        ? 'No tienes permiso para devolver activos de este tipo.'
        : (e?.response?.data?.message || 'No se pudo devolver el activo.'));
    } finally {
      setDevolviendo(null);
    }
  };

  return (
    <section className="rounded-v25-lg border border-v25-line bg-white p-4">
      <header className="mb-3 flex items-center justify-between border-b border-v25-line pb-2">
        <div className="flex items-center gap-2">
          <Boxes size={17} className="text-v25-brand-600" />
          <h4 className="text-[14px] font-semibold text-v25-ink-900">
            Material asignado {enSuPoder.length ? `(${enSuPoder.length})` : ''}
            {devueltos.length ? <span className="ml-1 text-[12px] font-normal text-v25-ink-500">· {devueltos.length} devuelto{devueltos.length > 1 ? 's' : ''}</span> : null}
          </h4>
        </div>
        {!!bajaAbierta && (
          <span className="inline-flex items-center gap-1 rounded-full bg-v25-warn-bg px-2 py-0.5 text-[11px] font-semibold text-v25-warn-fg">
            <CircleAlert size={12} /> Proceso de salida abierto
          </span>
        )}
      </header>

      <p className="mb-3 text-[12px] leading-relaxed text-v25-ink-500">
        Lo que consta en Inventario a nombre de esta persona, en su ámbito. Desde aquí se consulta
        y se devuelve; asignar se hace en <strong>Inventario</strong>.
      </p>

      {cargando && (
        <div className="flex items-center gap-2 p-6 text-[13px] text-v25-ink-500">
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      )}

      {/* «NO LO SABEMOS». Va ANTES de la tabla y con el texto del servidor: si saliera debajo de una
          lista vacía, quien mire ya habría concluido que no tiene nada. */}
      {!cargando && material && material.fiable === false && (
        <div className="mb-3 flex items-start gap-2 rounded-v25-md border border-v25-warn-line bg-v25-warn-bg px-3 py-3">
          <Lock size={15} className="mt-0.5 shrink-0 text-v25-warn-fg" />
          <p className="text-[12px] leading-relaxed text-v25-warn-fg">
            <strong>Inventario no puede saber qué material tiene esta persona.</strong>
            {' '}{material.motivo_no_fiable}
          </p>
        </div>
      )}

      {!cargando && material && material.fiable !== false && !items.length && (
        <p className="rounded-v25-md border border-dashed border-v25-line px-3 py-3 text-[12px] text-v25-ink-500">
          No tiene material asignado.
        </p>
      )}

      {!cargando && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-v25-line text-left text-[12px] font-semibold text-v25-ink-700">
                <th className="py-2 pr-3">Activo</th>
                <th className="py-2 pr-3">Referencia</th>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={`${m.asset_type_slug}-${m.asset_id}`}
                  className={`border-b border-v25-line last:border-0 ${m.devuelto ? 'text-v25-ink-500' : ''}`}>
                  <td className="py-2 pr-3 text-v25-ink-900">{m.tipo_nombre}</td>
                  <td className="py-2 pr-3 text-v25-ink-700">
                    {m.referencia || <span className="text-v25-ink-400">—</span>}
                  </td>
                  <td className="py-2 pr-3 text-v25-ink-700">{soloFecha(m.fecha)}</td>
                  <td className="py-2 pr-3">
                    {/* El orden importa: DEVUELTO manda sobre «pendiente de devolución». Con una baja
                        abierta, lo ya devuelto no está pendiente — está hecho. */}
                    {m.devuelto ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-v25-surface-sunken px-2 py-0.5 text-[11px] font-semibold text-v25-ink-500">
                        <Undo2 size={11} /> devuelto
                      </span>
                    ) : bajaAbierta ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-v25-warn-bg px-2 py-0.5 text-[11px] font-semibold text-v25-warn-fg">
                        pendiente de devolución
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-v25-surface-sunken px-2 py-0.5 text-[11px] font-semibold text-v25-ink-700">
                        {ETIQUETA_EVENTO[m.ultimo_evento] || m.ultimo_evento}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <div className="inline-flex items-center gap-3">
                      {puedeEditar && !m.devuelto && (
                        <button type="button" onClick={() => devolver(m)}
                          disabled={devolviendo === `${m.asset_type_slug}-${m.asset_id}`}
                          aria-label={`Devolver ${m.tipo_nombre}`}
                          className="inline-flex items-center gap-1 text-[12px] text-v25-ink-500 hover:text-v25-ink-900 disabled:opacity-50">
                          {devolviendo === `${m.asset_type_slug}-${m.asset_id}`
                            ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}
                          Devolver
                        </button>
                      )}
                      <a href={enlaceActivo(ambito, m.asset_type_slug, m.asset_id)}
                        className="inline-flex items-center gap-1 text-[12px] text-v25-brand-600 hover:underline">
                        <ExternalLink size={13} /> Ver en Inventario
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[12px] leading-relaxed text-v25-ink-500">
        Aparece lo que sigue en su poder y también <strong>lo que devolvió</strong>, con su fecha:
        la ficha responde qué ha tenido esta persona, no sólo qué tiene ahora. El detalle completo
        de cada activo está en su histórico, en <strong>Inventario</strong>.
        {bajaAbierta ? ' Con un proceso de salida abierto, lo que sigue asignado queda pendiente de devolución.' : ''}
      </p>
    </section>
  );
}

export default PestanaInventario;
